import React, { useEffect, useState, useMemo } from "react";
import UsersServices from "../../../services/utilisateur/UsersService";
import useEntreprises from "../../../hooks/entreprise/useEntrepries";
import useUsers from "../../../hooks/utilisateur/useUsers";
import { searchOptimizedData } from "../../../utils/recherche";
import Loader from "../../components/loader";
import "../../../assets/css/historiqueAlert.css";

const PAGE_SIZE_OPTIONS = [5, 10, 20, 50, 100, 150, 200];

const HistoriqueNavigation = () => {
  const [entreprises, setEntreprises] = useState([]);
  const { getAllEntreprises } = useEntreprises();
  const { getSuperviseurByEntrepriseId } = useUsers();
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState("");
  const [selectedDate, setSelectedDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  });
  const [captures, setCaptures] = useState([]); // données sources, jamais écrasées par la recherche
  const [displayedCaptures, setDisplayedCaptures] = useState([]); // ce qui est réellement affiché
  const [loading, setLoading] = useState(false);
  const [captureSearched, setCaptureSearched] = useState(false);
  const [entrepriseSelected, setEntrepriseSelected] = useState("");
  const [superviseursParEntreprise, setSuperviseursParEntreprise] = useState(
    [],
  );
  const userData = JSON.parse(localStorage.getItem("user"));
  const [superviseursSelected, setSuperviseursSelected] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);

  // ---- Pagination ----
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);

  useEffect(() => {
    getAllEntreprises()
      .then((res) => {
        if (Array.isArray(res.data.data)) {
          setEntreprises(res.data.data);
        } else {
          console.error("La réponse de l'API n'est pas un tableau.");
        }
      })
      .catch((err) => console.error("Erreur API :", err));
  }, []);

  useEffect(() => {
    setCaptures([]);
    setDisplayedCaptures([]);
    setCaptureSearched(false);
  }, [selectedUser, selectedDate]);

  const resetFilter = () => {
    setSelectedUser("");
    setSelectedDate("");
  };

  useEffect(() => {
    if (!userData) return;
    if (userData.role === 1) {
      if (entreprises.length > 0 && !entrepriseSelected) {
        setEntrepriseSelected(entreprises[0]._id);
      }
    } else {
      setEntrepriseSelected(userData.entreprise);
      setSuperviseursSelected([userData._id]);
    }
  }, [entreprises, entrepriseSelected]);

  useEffect(() => {
    const fetchAllUsers = async () => {
      try {
        const response = await UsersServices.getAll();
        const usersRole3 = response.data.data.filter((user) => user.role === 3);
        setUsers(usersRole3);
      } catch (error) {
        console.error("Erreur lors de la récupération des utilisateurs", error);
      }
    };

    fetchAllUsers();
  }, []);

  useEffect(() => {
    if (!userData) return;
    if (userData.role === 1) {
      if (entrepriseSelected) {
        getSuperviseurByEntrepriseId(entrepriseSelected, (data) => {
          setSuperviseursParEntreprise(data || []);
        });
      } else {
        setSuperviseursParEntreprise([]);
      }
    }
  }, [entrepriseSelected]);

  useEffect(() => {
    const filtered = users.filter((user) => {
      const isFromEntreprise = user?.entreprise?._id === entrepriseSelected;

      const isSuperviseurOK =
        superviseursSelected.length === 0 ||
        (user.gerant &&
          user.gerant.some((gId) => superviseursSelected.includes(gId)));

      return isFromEntreprise && isSuperviseurOK;
    });

    setFilteredUsers(filtered);
  }, [users, entrepriseSelected, superviseursSelected]);

  const fetchCaptures = async () => {
    if (!selectedUser || !selectedDate) {
      console.warn("Tous les champs ne sont pas remplis");
      return;
    }

    const dateParts = selectedDate.split("-");
    const formattedDate = `${dateParts[2]}-${dateParts[1]}-${dateParts[0]}`;

    const data = {
      id: selectedUser,
      hour: formattedDate,
    };
    setLoading(true);

    try {
      const response = await UsersServices.getCapturesNavigation(data);
      const donnees = response.data?.data?.historique?.donnees;

      if (donnees && donnees.length > 0) {
        setCaptures(donnees);
        setDisplayedCaptures(donnees);
        setCaptureSearched(false);
      } else {
        setCaptures([]);
        setDisplayedCaptures([]);
        setCaptureSearched(true);
      }
    } catch (error) {
      console.error(
        "Erreur lors de la récupération des historiques de navigation",
        error,
      );
      setCaptures([]);
      setDisplayedCaptures([]);
      setCaptureSearched(true);
    } finally {
      setLoading(false);
      setCurrentPage(1); // on repart toujours de la page 1 après une nouvelle recherche
    }
  };

  const handleFilterSubmit = (e) => {
    e.preventDefault();
    fetchCaptures();
  };

  const handleSearchChange = (value) => {
    setCurrentPage(1); // évite de rester sur une page qui n'existe plus après filtrage
    if (!value) {
      setDisplayedCaptures(captures); // recherche vidée → on restaure la liste complète
      return;
    }
    setDisplayedCaptures(searchOptimizedData(value, captures));
  };

  const formatDateNavigation = (date) => {
    if (!date) return "date non valide";
    return date.split("T")[0] + " à " + date.split("T")[1].split(".")[0];
  };

  // ---- Pagination dérivée de displayedCaptures ----
  const totalItems = displayedCaptures.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

  // Sécurité : si la page courante dépasse le nombre de pages dispo (ex: après filtre), on corrige
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  const paginatedCaptures = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return displayedCaptures.slice(start, start + pageSize);
  }, [displayedCaptures, currentPage, pageSize]);

  const handlePageSizeChange = (value) => {
    setPageSize(value === "all" ? totalItems || 1 : Number(value));
    setCurrentPage(1);
  };

  const goToPage = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  // Génère une liste de pages compacte avec "..." si beaucoup de pages
  const getPageNumbers = () => {
    const delta = 1;
    const range = [];

    for (
      let i = Math.max(2, currentPage - delta);
      i <= Math.min(totalPages - 1, currentPage + delta);
      i++
    ) {
      range.push(i);
    }

    if (currentPage - delta > 2) {
      range.unshift("...");
    }
    if (currentPage + delta < totalPages - 1) {
      range.push("...");
    }

    range.unshift(1);
    if (totalPages > 1) range.push(totalPages);

    return [...new Set(range)];
  };

  return (
    <div className="container-fluid">
      {/* TITLE */}
      <div className="d-flex align-items-center justify-content-between mb-4">
        <h2 className="h4 mb-0">Historiques Navigation</h2>
      </div>

      {/* FILTRES */}
      <div className="card shadow-sm mb-4">
        <div className="card-header bg-white">
          <h3 className="h6 mb-0">Filtrer les historiques de navigation</h3>
        </div>

        <div className="card-body">
          <form onSubmit={handleFilterSubmit}>
            <div className="row g-3">
              {userData?.role === 1 && (
                <>
                  <div className="col-12 col-md-6">
                    <label className="form-label fw-semibold small">
                      Entreprise
                    </label>
                    <select
                      className="form-select"
                      value={entrepriseSelected}
                      onChange={(e) => setEntrepriseSelected(e.target.value)}
                      required
                    >
                      {entreprises.map((ent) => (
                        <option key={ent._id} value={ent._id}>
                          {ent.nom}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-12 col-md-6">
                    <label className="form-label fw-semibold small">
                      Superviseurs
                    </label>
                    <div
                      className="border rounded-3 px-2 py-2"
                      style={{
                        minHeight: "45px",
                        maxHeight: "150px",
                        overflowY: "auto",
                        backgroundColor: entrepriseSelected
                          ? "#fff"
                          : "#f8f9fa",
                        cursor: entrepriseSelected ? "default" : "not-allowed",
                      }}
                    >
                      {superviseursParEntreprise.length > 0 ? (
                        superviseursParEntreprise.map((sup) => (
                          <div key={sup._id} className="form-check">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              id={`sup-${sup._id}`}
                              value={sup._id}
                              disabled={!entrepriseSelected}
                              checked={superviseursSelected.includes(sup._id)}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                setSuperviseursSelected((prev) =>
                                  checked
                                    ? [...prev, sup._id]
                                    : prev.filter((id) => id !== sup._id),
                                );
                              }}
                            />
                            <label
                              className="form-check-label ms-1"
                              htmlFor={`sup-${sup._id}`}
                            >
                              {sup.nom}
                            </label>
                          </div>
                        ))
                      ) : (
                        <p className="text-muted mb-0 small">
                          Aucun superviseur disponible
                        </p>
                      )}
                    </div>
                  </div>
                </>
              )}

              <div className="col-12 col-md-6">
                <label htmlFor="user" className="form-label fw-semibold small">
                  Utilisateur
                </label>
                <select
                  id="user"
                  className="form-select"
                  value={selectedUser}
                  onChange={(e) => setSelectedUser(e.target.value)}
                  required
                >
                  <option value="">Sélectionner un utilisateur</option>
                  {filteredUsers.length === 0 && (
                    <option disabled>Aucun utilisateur trouvé</option>
                  )}
                  {filteredUsers.map((user) => (
                    <option key={user._id} value={user._id}>
                      {user.nom} {user.prenom}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-12 col-md-6">
                <label htmlFor="date" className="form-label fw-semibold small">
                  Date
                </label>
                <input
                  type="date"
                  id="date"
                  className="form-control"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="d-flex gap-2 mt-4">
              <button
                type="button"
                className="btn btn-outline-danger flex-fill"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  resetFilter();
                }}
              >
                Réinitialiser
              </button>
              <button type="submit" className="btn btn-primary flex-fill">
                Filtrer
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* RESULTATS */}
      <div className="card shadow-sm mb-4">
        <div className="card-body">
          {loading ? (
            <Loader />
          ) : captures && captures.length > 0 ? (
            <>
              <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
                <span className="fw-semibold">
                  Liste des navigations — {captures.length} lien(s)
                </span>

                <div
                  className="d-flex align-items-center gap-2 px-3 py-2 rounded-3"
                  style={{ backgroundColor: "rgba(226, 232, 240, 0.6)" }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="#818CF8"
                    style={{ width: "18px", height: "18px" }}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z"
                    />
                  </svg>
                  <input
                    type="text"
                    className="border-0 bg-transparent"
                    style={{ outline: "none" }}
                    placeholder="Recherche"
                    onChange={(ev) => handleSearchChange(ev.target.value)}
                  />
                </div>
              </div>

              {/* TABLEAU BOOTSTRAP RESPONSIVE */}
              <div className="table-responsive rounded-3 border">
                <table className="table table-hover align-middle mb-0 custom-bs-table">
                  <thead>
                    <tr>
                      <th style={{ minWidth: 220 }}>URL</th>
                      <th style={{ minWidth: 180 }}>Titre</th>
                      <th style={{ minWidth: 130 }}>Navigateur</th>
                      <th style={{ minWidth: 170 }}>Date de visite</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedCaptures.length > 0 ? (
                      paginatedCaptures.map((row, idx) => (
                        <tr key={row._id || row.url + idx}>
                          <td
                            className="text-truncate"
                            style={{ maxWidth: 280 }}
                            title={row.url}
                          >
                            {row.url ? (
                              <a
                                href={row.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="fw-semibold text-decoration-none"
                              >
                                {row.url}
                              </a>
                            ) : (
                              "-"
                            )}
                          </td>
                          <td
                            className="text-truncate"
                            style={{ maxWidth: 220 }}
                            title={row.title}
                          >
                            {row.title || "-"}
                          </td>
                          <td>{row.navigateur || "-"}</td>
                          <td>{formatDateNavigation(row.date)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={4} className="text-center text-muted py-4">
                          Aucun résultat pour cette recherche.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* PAGINATION */}
              <div className="d-flex flex-wrap justify-content-between align-items-center mt-3 gap-2">
                <div className="d-flex align-items-center gap-2">
                  <label className="small text-muted mb-0" htmlFor="pageSize">
                    Taille de page
                  </label>
                  <select
                    id="pageSize"
                    className="form-select form-select-sm"
                    style={{ width: "auto" }}
                    value={
                      pageSize >= totalItems && totalItems > 0
                        ? "all"
                        : pageSize
                    }
                    onChange={(e) => handlePageSizeChange(e.target.value)}
                  >
                    {PAGE_SIZE_OPTIONS.map((size) => (
                      <option key={size} value={size}>
                        {size}
                      </option>
                    ))}
                    <option value="all">Tous</option>
                  </select>
                  <span className="small text-muted d-none d-sm-inline">
                    {totalItems === 0
                      ? "0 résultat"
                      : `${(currentPage - 1) * pageSize + 1}–${Math.min(
                          currentPage * pageSize,
                          totalItems,
                        )} sur ${totalItems}`}
                  </span>
                </div>

                <nav aria-label="Pagination du tableau">
                  <ul className="pagination pagination-sm mb-0 flex-wrap">
                    <li
                      className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                    >
                      <button
                        type="button"
                        className="page-link"
                        onClick={() => goToPage(1)}
                        title="Première"
                      >
                        «
                      </button>
                    </li>
                    <li
                      className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                    >
                      <button
                        type="button"
                        className="page-link"
                        onClick={() => goToPage(currentPage - 1)}
                        title="Précédente"
                      >
                        ‹
                      </button>
                    </li>

                    {getPageNumbers().map((page, idx) =>
                      page === "..." ? (
                        <li
                          key={`ellipsis-${idx}`}
                          className="page-item disabled"
                        >
                          <span className="page-link">…</span>
                        </li>
                      ) : (
                        <li
                          key={page}
                          className={`page-item ${
                            page === currentPage ? "active" : ""
                          }`}
                        >
                          <button
                            type="button"
                            className="page-link"
                            onClick={() => goToPage(page)}
                          >
                            {page}
                          </button>
                        </li>
                      ),
                    )}

                    <li
                      className={`page-item ${
                        currentPage === totalPages ? "disabled" : ""
                      }`}
                    >
                      <button
                        type="button"
                        className="page-link"
                        onClick={() => goToPage(currentPage + 1)}
                        title="Suivante"
                      >
                        ›
                      </button>
                    </li>
                    <li
                      className={`page-item ${
                        currentPage === totalPages ? "disabled" : ""
                      }`}
                    >
                      <button
                        type="button"
                        className="page-link"
                        onClick={() => goToPage(totalPages)}
                        title="Dernière"
                      >
                        »
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            </>
          ) : (
            captureSearched && (
              <div className="text-center py-4">
                <p style={{ color: "#ff6f61", fontSize: "18px" }}>
                  <i
                    className="fas fa-image-slash"
                    style={{ fontSize: "24px", marginRight: "10px" }}
                  ></i>
                  Désolé, aucun historique de navigation trouvé pour cet
                  utilisateur et cette date.
                </p>
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default HistoriqueNavigation;
